import { Button, Radio, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import CreateRegistryApplication from './application';
import CreateRegistryNote from './note';
import CreateRegistryPlanting from './planting';
import type { RegistryAddStep } from './types';
import CreateRegistryVisitReport from './visitReport';

type RegistryType = Exclude<RegistryAddStep, 'select'>;

const showRegistrySavedToast = () => {
    enqueueSnackbar(_('¡Listo! El registro se guardó en el seguimiento del lote.'), { variant: 'success' });
};

const AddRegistryModal = ({
    open,
    onClose,
    fieldId,
    cropMonitoring,
    initialStep = 'select',
    onHarvest,
}: {
    open: boolean;
    onClose: () => void;
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    /** Skips the type picker and opens directly on this step, e.g. from a CTA specific to one registry type. */
    initialStep?: RegistryAddStep;
    /** "Cosecha" opens the existing harvest-report flow instead of a step of its own. */
    onHarvest?: () => void;
}) => {
    const OPTIONS: { type: RegistryType; title: string; subtitle: string; disabled?: boolean }[] = [
        {
            type: 'planting',
            title: _('Siembra'),
            subtitle: _('Agregar cultivo, producto y densidad'),
            disabled: !!cropMonitoring?.id,
        },
        {
            type: 'application',
            title: _('Aplicaciones'),
            subtitle: _('Registrar protección de cultivos o fertilización'),
        },
        {
            type: 'visit',
            title: _('Visita al productor'),
            subtitle: _('Crear un informe con observaciones, fotos y datos de la visita'),
        },
        { type: 'note', title: _('Nota'), subtitle: _('Agregar una observación sobre el lote') },
        {
            type: 'harvest',
            title: _('Cosecha'),
            subtitle: _('Generar reporte con resultados y datos de la cosecha'),
            disabled: !cropMonitoring?.id || !!cropMonitoring?.harvestDate,
        },
    ];
    const [step, setStep] = useState<RegistryAddStep>(initialStep);
    const [selectedType, setSelectedType] = useState<RegistryType>('note');

    // The modal stays mounted across opens (only `open` toggles), so re-apply `initialStep`
    // each time it's opened rather than only on first mount.
    useEffect(() => {
        if (open) {
            setStep(initialStep);
            if (initialStep !== 'select') setSelectedType(initialStep);
        }
    }, [open, initialStep]);

    const handleClose = () => {
        setStep('select');
        setSelectedType('note');
        onClose();
    };

    const handleRegistrySaved = () => {
        showRegistrySavedToast();
        handleClose();
    };

    const handleContinue = () => {
        const option = OPTIONS.find((o) => o.type === selectedType);
        if (option?.disabled) return;
        if (selectedType === 'harvest') {
            handleClose();
            onHarvest?.();
            return;
        }
        setStep(selectedType);
    };

    if (step === 'note') {
        return (
            <CreateRegistryNote
                open={open}
                onClose={handleClose}
                onSuccess={handleRegistrySaved}
                setStep={setStep}
                fieldId={fieldId}
                cropMonitoring={cropMonitoring}
            />
        );
    }

    if (step === 'planting') {
        return (
            <CreateRegistryPlanting
                open={open}
                onClose={handleClose}
                onSuccess={handleRegistrySaved}
                setStep={setStep}
                fieldId={fieldId}
                cropMonitoring={cropMonitoring}
            />
        );
    }

    if (step === 'application') {
        return (
            <CreateRegistryApplication
                open={open}
                onClose={handleClose}
                onSuccess={handleRegistrySaved}
                setStep={setStep}
                fieldId={fieldId}
            />
        );
    }

    if (step === 'visit') {
        return (
            <CreateRegistryVisitReport
                open={open}
                onClose={handleClose}
                setStep={setStep}
                fieldId={fieldId}
                cropMonitoring={cropMonitoring}
            />
        );
    }

    return (
        <ModalV2
            open={open}
            onClose={handleClose}
            title={_('Seleccioná qué querés registrar en el lote')}
            bodyProps={{ alignItems: 'stretch', textAlign: 'left', spacing: 2 }}
            actionsProps={{ justifyContent: 'flex-end' }}
            actions={
                <>
                    <Button variant='text' color='secondary' onClick={handleClose}>
                        {_('Cancelar')}
                    </Button>
                    <Button variant='contained' color='secondary' onClick={handleContinue}>
                        {_('Continuar')}
                    </Button>
                </>
            }
        >
            <Stack spacing={1}>
                {OPTIONS.map((option) => {
                    const isSelected = selectedType === option.type;
                    return (
                        <Stack
                            key={option.type}
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            spacing={2}
                            onClick={() => !option.disabled && setSelectedType(option.type)}
                            sx={{
                                border: 1,
                                borderColor: isSelected ? 'secondary.main' : 'primary.mui.stroke',
                                borderRadius: 2,
                                p: 2,
                                cursor: option.disabled ? 'default' : 'pointer',
                                opacity: option.disabled ? 0.5 : 1,
                            }}
                        >
                            <Stack spacing={0.5}>
                                <Typography variant='h6' color='primary'>
                                    {option.title}
                                </Typography>
                                <Typography variant='body2' color='primary.mui.inactive'>
                                    {option.subtitle}
                                </Typography>
                            </Stack>
                            <Radio
                                checked={isSelected}
                                disabled={option.disabled}
                                color='secondary'
                                onChange={() => setSelectedType(option.type)}
                            />
                        </Stack>
                    );
                })}
            </Stack>
        </ModalV2>
    );
};

export default AddRegistryModal;
